/**
 * /erp/manufacturing/{jobId} — manufacturing job detail (Manager+).
 *
 * Layout:
 *
 *   * Header — code, status badge, lifecycle buttons appropriate for
 *     the current status (planned → start, started → complete or
 *     cancel, completed/cancelled → no-ops).
 *   * Summary — product/qty/warehouse/notes/timestamps.
 *
 * Manufacturing does not auto-deduct raw materials — reduce them
 * manually via inventory adjustments. Starting a job only marks it in
 * progress; completing it credits the produced product to the job's
 * warehouse. Each lifecycle button asks for confirmation before
 * firing, and every transition is recorded on the audit log.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  Ban,
  Box,
  Check,
  Edit,
  Factory,
  Loader2,
  Pencil,
  Play,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import {
  ApiError,
  type ErpMfgJob,
  type ErpMfgJobUpdate,
  erpApi,
  isErpCatalogConflict,
  isErpMfgConflict,
} from "../lib/api";
import { useMfgJob, useWarehouses } from "../lib/erp";
import { toast } from "../lib/toast";
import { cleanQtyInput, formatQty, sanitizeQtyInput } from "../lib/units";
import { CustomFieldsPanel } from "../components/custom-fields/CustomFieldsPanel";
import { StatusBadge } from "./_app.erp.manufacturing.index";

export const Route = createFileRoute("/_app/erp/manufacturing/$jobId")({
  component: ManufacturingJobDetailPage,
});

type Pending =
  | { kind: "start" }
  | { kind: "complete" }
  | { kind: "cancel" }
  | { kind: "delete" }
  | { kind: "edit" }
  | null;

function ManufacturingJobDetailPage() {
  const { jobId } = useParams({ from: "/_app/erp/manufacturing/$jobId" });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const jobQ = useMfgJob(jobId);
  const [pending, setPending] = useState<Pending>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const onMutationError = (err: unknown) => {
    if (err instanceof ApiError) {
      const detail = err.detail;
      if (isErpMfgConflict(detail) || isErpCatalogConflict(detail)) {
        setActionError(detail.message);
        return;
      }
    }
    const msg = err instanceof ApiError ? err.message : "Action failed.";
    setActionError(msg);
    toast.error(msg);
  };

  const startMutation = useMutation({
    mutationFn: () => erpApi.manufacturing.start(jobId),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "manufacturing-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["erp", "inventory"] });
      toast.success(`Job ${job.code} started.`);
      setPending(null);
    },
    onError: onMutationError,
  });

  const completeMutation = useMutation({
    mutationFn: () => erpApi.manufacturing.complete(jobId),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "manufacturing-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["erp", "inventory"] });
      toast.success(`Job ${job.code} completed. Stock added.`);
      setPending(null);
    },
    onError: onMutationError,
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) =>
      erpApi.manufacturing.cancel(jobId, reason ? { reason } : undefined),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "manufacturing-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["erp", "inventory"] });
      toast.success(`Job ${job.code} cancelled.`);
      setPending(null);
    },
    onError: onMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => erpApi.manufacturing.remove(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["erp", "manufacturing-jobs"] });
      toast.success("Job deleted.");
      navigate({ to: "/erp/manufacturing" });
    },
    onError: onMutationError,
  });

  if (jobQ.isLoading) {
    return (
      <div className="mx-auto max-w-4xl p-4 sm:p-6">
        <p className="flex items-center gap-2 text-sm opacity-60">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Loading job…
        </p>
      </div>
    );
  }
  if (jobQ.error) {
    return (
      <div className="mx-auto max-w-4xl p-4 sm:p-6">
        <Link
          to="/erp/manufacturing"
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:underline"
        >
          <ArrowLeft className="size-3" aria-hidden /> Manufacturing jobs
        </Link>
        <p className="mt-3 text-sm text-red-700 dark:text-red-300">
          Could not load job: {(jobQ.error as Error).message}
        </p>
      </div>
    );
  }
  const job = jobQ.data;
  if (!job) return null;

  const canStart = job.status === "planned";
  const canComplete = job.status === "started";
  const canCancel = job.status === "planned" || job.status === "started";
  const canDelete = job.status === "planned";
  const canEdit = job.status === "planned";

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-6">
      <Link
        to="/erp/manufacturing"
        className="inline-flex items-center gap-1 text-xs opacity-70 hover:underline"
      >
        <ArrowLeft className="size-3" aria-hidden /> Manufacturing jobs
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Factory className="size-5 opacity-70" aria-hidden />
            <span className="rounded-md bg-black/5 px-2 py-0.5 font-mono text-base dark:bg-white/10">
              {job.code}
            </span>
            <StatusBadge status={job.status} />
          </h1>
          <p className="text-sm opacity-60">
            Producing <span className="font-medium">{formatQty(job.qty, job.product_uom)}</span>{" "}
            <span className="opacity-70">{job.product_uom}</span> of{" "}
            <span className="font-medium">{job.product_name}</span>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={() => setPending({ kind: "edit" })}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
            >
              <Pencil className="size-4" aria-hidden /> Edit
            </button>
          )}
          {canStart && (
            <button
              type="button"
              onClick={() => setPending({ kind: "start" })}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500"
            >
              <Play className="size-4" aria-hidden /> Start
            </button>
          )}
          {canComplete && (
            <button
              type="button"
              onClick={() => setPending({ kind: "complete" })}
              className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              <Check className="size-4" aria-hidden /> Complete
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={() => setPending({ kind: "cancel" })}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300"
            >
              <Ban className="size-4" aria-hidden /> Cancel
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => setPending({ kind: "delete" })}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300"
            >
              <Trash2 className="size-4" aria-hidden /> Delete
            </button>
          )}
        </div>
      </header>

      {actionError && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-200">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div className="flex-1">
            <p className="font-medium">Action blocked</p>
            <p className="mt-0.5 text-xs">{actionError}</p>
          </div>
          <button
            type="button"
            onClick={() => setActionError(null)}
            aria-label="Dismiss"
            className="rounded p-0.5 hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      )}

      <SummaryGrid job={job} />

      {pending?.kind === "start" && (
        <ConfirmDialog
          title={`Start ${job.code}?`}
          body={
            <div className="space-y-2 text-sm opacity-80">
              <p>
                Starting marks the job in progress. Raw materials are{" "}
                <span className="font-medium">not deducted automatically</span> — reduce them
                manually via inventory adjustments.
              </p>
              <p>Completing the job later credits the finished product to the warehouse.</p>
            </div>
          }
          confirmLabel="Start now"
          confirmColor="emerald"
          onCancel={() => setPending(null)}
          onConfirm={() => startMutation.mutate()}
          loading={startMutation.isPending}
        />
      )}

      {pending?.kind === "complete" && (
        <ConfirmDialog
          title={`Complete ${job.code}?`}
          body={
            <p className="text-sm opacity-80">
              This credits{" "}
              <span className="font-medium">{formatQty(job.qty, job.product_uom)}</span>{" "}
              <span className="opacity-70">{job.product_uom}</span> of{" "}
              <span className="font-medium">{job.product_name}</span> to{" "}
              <span className="rounded-md bg-black/5 px-1 py-0.5 font-mono text-[11px] dark:bg-white/10">
                {job.warehouse_code}
              </span>
              . The job becomes read-only.
            </p>
          }
          confirmLabel="Complete job"
          confirmColor="accent"
          onCancel={() => setPending(null)}
          onConfirm={() => completeMutation.mutate()}
          loading={completeMutation.isPending}
        />
      )}

      {pending?.kind === "cancel" && (
        <CancelDialog
          job={job}
          onCancel={() => setPending(null)}
          onConfirm={(reason) => cancelMutation.mutate(reason)}
          loading={cancelMutation.isPending}
        />
      )}

      <CustomFieldsPanel entity="manufacturing_jobs" recordKey={job.id} />

      {pending?.kind === "delete" && (
        <ConfirmDialog
          title={`Delete ${job.code}?`}
          body={
            <p className="text-sm opacity-80">
              Only planned jobs can be deleted. This is permanent — the job row and any audit
              entries we've written go away. If you've already started it, cancel instead so the
              history is preserved.
            </p>
          }
          confirmLabel="Delete job"
          confirmColor="red"
          onCancel={() => setPending(null)}
          onConfirm={() => deleteMutation.mutate()}
          loading={deleteMutation.isPending}
        />
      )}

      {pending?.kind === "edit" && (
        <EditDialog
          job={job}
          onClose={() => setPending(null)}
          onSaved={() => {
            queryClient.invalidateQueries({
              queryKey: ["erp", "manufacturing-jobs"],
            });
            setPending(null);
          }}
          onError={onMutationError}
        />
      )}
    </div>
  );
}

function SummaryGrid({ job }: { job: ErpMfgJob }) {
  const rows: { label: string; value: React.ReactNode }[] = [
    {
      label: "Product",
      value: (
        <div className="flex items-center gap-2">
          <Box className="size-3 opacity-60" aria-hidden />
          <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
            {job.product_code}
          </span>
          <span>{job.product_name}</span>
        </div>
      ),
    },
    {
      label: "Qty",
      value: (
        <span className="font-mono">
          {formatQty(job.qty, job.product_uom)}{" "}
          <span className="opacity-60">{job.product_uom}</span>
        </span>
      ),
    },
    {
      label: "Warehouse",
      value: (
        <span>
          <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
            {job.warehouse_code}
          </span>{" "}
          {job.warehouse_name}
        </span>
      ),
    },
    {
      label: "Created",
      value: (
        <span>
          {new Date(job.created_at).toLocaleString()}
          {job.created_by_name && <span className="ml-1 opacity-70">by {job.created_by_name}</span>}
        </span>
      ),
    },
  ];
  if (job.started_at) {
    rows.push({
      label: "Started",
      value: <span>{new Date(job.started_at).toLocaleString()}</span>,
    });
  }
  if (job.completed_at) {
    rows.push({
      label: "Completed",
      value: <span>{new Date(job.completed_at).toLocaleString()}</span>,
    });
  }
  if (job.cancelled_at) {
    rows.push({
      label: "Cancelled",
      value: <span>{new Date(job.cancelled_at).toLocaleString()}</span>,
    });
  }
  if (job.cancel_reason) {
    rows.push({
      label: "Cancel reason",
      value: <span className="opacity-90">{job.cancel_reason}</span>,
    });
  }
  if (job.notes) {
    rows.push({
      label: "Notes",
      value: <span className="whitespace-pre-wrap opacity-90">{job.notes}</span>,
    });
  }
  return (
    <div className="rounded-xl border border-[var(--border)]">
      <dl className="divide-y divide-[var(--border)] text-sm">
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-[max-content_1fr] gap-3 px-4 py-2">
            <dt className="text-xs uppercase tracking-wider opacity-60">{r.label}</dt>
            <dd>{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  confirmColor,
  onCancel,
  onConfirm,
  loading,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  confirmColor: "accent" | "red" | "emerald";
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const colorClass = {
    accent: "bg-[var(--accent)] text-white hover:opacity-90",
    red: "bg-red-600 text-white hover:bg-red-500",
    emerald: "bg-emerald-600 text-white hover:bg-emerald-500",
  }[confirmColor];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // biome-ignore lint/a11y/useSemanticElements: <dialog> doesn't compose with our backdrop click pattern.
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-black/40"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-md space-y-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-5 shadow-lg">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {body}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden /> Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${colorClass}`}
          >
            {loading ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Check className="size-4" aria-hidden />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelDialog({
  job,
  onCancel,
  onConfirm,
  loading,
}: {
  job: ErpMfgJob;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  loading: boolean;
}) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onConfirm(reason.trim());
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // biome-ignore lint/a11y/useSemanticElements: <dialog> doesn't compose with our backdrop click pattern.
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-black/40"
        onClick={onCancel}
      />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-md space-y-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-5 shadow-lg"
      >
        <h2 className="text-lg font-semibold tracking-tight">Cancel {job.code}?</h2>
        <p className="text-sm opacity-80">
          {job.status === "started"
            ? "This marks the job cancelled. Raw materials are managed manually, so there is no inventory effect."
            : "Cancelling a planned job has no inventory effect."}
        </p>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Reason (optional)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="e.g. machine breakdown, ran out of raws, …"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden /> Keep job
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Ban className="size-4" aria-hidden />
            )}
            Cancel job
          </button>
        </div>
      </form>
    </div>
  );
}

function EditDialog({
  job,
  onClose,
  onSaved,
  onError,
}: {
  job: ErpMfgJob;
  onClose: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const warehousesQ = useWarehouses();
  const [qty, setQty] = useState(cleanQtyInput(job.qty, job.product_uom));
  const [warehouseId, setWarehouseId] = useState(job.warehouse_id);
  const [notes, setNotes] = useState(job.notes ?? "");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: (body: ErpMfgJobUpdate) => erpApi.manufacturing.update(job.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["erp", "manufacturing-jobs"] });
      toast.success("Job updated.");
      onSaved();
    },
    onError,
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: ErpMfgJobUpdate = {};
    if (qty !== job.qty) body.qty = qty;
    if (warehouseId !== job.warehouse_id) body.warehouse_id = warehouseId;
    const trimmedNotes = notes.trim();
    if (trimmedNotes !== (job.notes ?? "")) {
      body.notes = trimmedNotes ? trimmedNotes : null;
    }
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    mutation.mutate(body);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // biome-ignore lint/a11y/useSemanticElements: <dialog> doesn't compose with our backdrop click pattern.
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-black/40"
        onClick={onClose}
      />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-md space-y-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-5 shadow-lg"
      >
        <h2 className="text-lg font-semibold tracking-tight">Edit {job.code}</h2>
        <p className="text-xs opacity-60">
          Only planned jobs can be edited. Once started, the qty / warehouse freeze for audit-trail
          integrity — cancel and re-create instead.
        </p>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Qty</span>
          <input
            type="text"
            value={qty}
            onChange={(e) => setQty(sanitizeQtyInput(e.target.value, job.product_uom))}
            inputMode="decimal"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Warehouse</span>
          <select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          >
            {(warehousesQ.data?.items ?? [])
              .filter((w) => w.is_active || w.id === job.warehouse_id)
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} — {w.name}
                  {!w.is_active ? " (inactive)" : ""}
                </option>
              ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden /> Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Edit className="size-4" aria-hidden />
            )}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}
